import Button from "@/components/button/Button.tsx";
import {Switch} from "@/components/switch/Switch.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, TextAlignment} from "@/components/table/Table.types.ts";
import {Heading} from "@/components/typography/heading/Heading.tsx";
import {
  DEFAULT_SLACK_CUSTOMIZATION,
  SLACK_SECTION_KEYS,
  SLACK_SECTION_LABELS,
  SlackAlertType,
  SlackCustomizationState,
  SlackSectionToggles,
} from "@/pages/integrations/edit/subForms/slackInfo.ts";

interface SlackMessageCustomizationProps {
  value: SlackCustomizationState;
  onChange: (next: SlackCustomizationState) => void;
}

interface SectionRow {
  sectionKey: keyof SlackSectionToggles;
  label: string;
  description?: string;
  downChecked: boolean;
  upChecked: boolean;
}

export const SlackMessageCustomization = ({value, onChange}: SlackMessageCustomizationProps) => {
  const toggle = (alertType: SlackAlertType, sectionKey: keyof SlackSectionToggles) => {
    onChange({
      ...value,
      [alertType]: {
        ...value[alertType],
        [sectionKey]: !value[alertType][sectionKey],
      },
    });
  };

  const reset = () => onChange(DEFAULT_SLACK_CUSTOMIZATION);

  const rows: SectionRow[] = SLACK_SECTION_KEYS.map((sectionKey) => {
    const meta = SLACK_SECTION_LABELS[sectionKey];
    return {
      sectionKey,
      label: meta.label,
      description: meta.description,
      downChecked: value.down?.[sectionKey] ?? false,
      upChecked: value.up?.[sectionKey] ?? false,
    };
  });

  const switchCell = (alertType: SlackAlertType, alertLabel: string) => (row: SectionRow) => (
    <Switch
      id={`slack-toggle-${alertType}-${row.sectionKey}`}
      checked={alertType === "down" ? row.downChecked : row.upChecked}
      onChange={() => toggle(alertType, row.sectionKey)}
      aria-label={`${row.label} in ${alertLabel} alerts`}
    />
  );

  const specs: ColumnSpec<SectionRow>[] = [
    {
      fieldName: "section",
      fieldTitle: "Section",
      accessor: (row: SectionRow) => (
        <>
          <div className="pulseTextBodySmallBold">{row.label}</div>
          {row.description && <div className="pulseTextBodyTiny pulse-color-gray-600 mt-1">{row.description}</div>}
        </>
      ),
    },
    {
      fieldName: "down",
      fieldTitle: "Down",
      meta: {
        headerTextAlignment: TextAlignment.Center,
        headerClassName: "text-center",
        columnClassName: "text-center",
      },
      accessor: switchCell("down", "Down"),
    },
    {
      fieldName: "up",
      fieldTitle: "Up",
      meta: {
        headerTextAlignment: TextAlignment.Center,
        headerClassName: "text-center",
        columnClassName: "text-center",
      },
      accessor: switchCell("up", "Up"),
    },
  ];

  return (
    <div className="mt-4">
      <div className="d-flex align-items-baseline justify-content-between gap-3 mb-2">
        <Heading as="h6" className="pulseTextBodySmallBold m-0">
          Message customization
        </Heading>
      </div>

      <Table rows={rows} specs={specs} getRowId={(row) => row.sectionKey} />

      <div className="d-flex justify-content-end mt-2">
        <Button variant="linkBlue" typography="linkTiny" onClick={reset}>
          Reset to defaults
        </Button>
      </div>
    </div>
  );
};
